import { Alert, Button, HFlow, Icon, ModalBody, ModalFooter, VFlow } from 'bold-ui'
import { ButtonLink, PrivateRoute } from 'components/route'
import { TitleGroup } from 'components/TitleGroup'
import theme from 'config/theme'
import { css } from 'emotion'
import { AcessoCbo, SexoEnum } from 'graphql/types.generated'
import { isEmpty } from 'lodash'
import React, { useCallback, useMemo } from 'react'
import { FieldArrayRenderProps } from 'react-final-form-arrays'
import { Switch, useRouteMatch } from 'react-router'

import MedicamentoCard from '../components/MedicamentoCard'
import { MedicamentoFormModel } from '../MedicamentoFormModel'
import ImpressaoPrescricaoModal from './ImpressaoPrescricaoModal'
import PrescricaoMedicamentoForm from './PrescricaoMedicamentoForm'

export interface AdicionarPrescricaoFieldArrayProps extends FieldArrayRenderProps<MedicamentoFormModel, any> {
  medicamentoToEdit: MedicamentoFormModel
  medicamentosToReplicate: MedicamentoFormModel[]
  atendimentoId: ID
  cidadaoDataNascimento: LocalDate
  cidadaoSexo: SexoEnum
  setMedicamentoToEdit(medicamento: MedicamentoFormModel): void
  onClearMedicamentosToReplicate(): void
  onChangeMedicamentosToReplicate(medicamentos: MedicamentoFormModel[]): void
  onCloseModal(): void
}

export default function AdicionarPrescricaoFieldArray(props: AdicionarPrescricaoFieldArrayProps) {
  const {
    fields: { push, remove, update, value },
    onChangeMedicamentosToReplicate,
    onClearMedicamentosToReplicate,
    medicamentosToReplicate,
    medicamentoToEdit,
    onCloseModal,
    setMedicamentoToEdit,
    atendimentoId,
    cidadaoDataNascimento,
    cidadaoSexo,
  } = props
  const match = useRouteMatch()

  const handleOnChangeEditedMedicamento = useCallback(
    (editedMedicamento: MedicamentoFormModel) => setMedicamentoToEdit(editedMedicamento),
    [setMedicamentoToEdit]
  )

  const handleOnRemoveMedicamento = useCallback(
    (medicamentoToRemove: MedicamentoFormModel) => {
      const index = value.findIndex((item) => item.id === medicamentoToRemove.id)
      remove(index)
      onClearMedicamentosToReplicate()
      if (medicamentoToEdit?.id === medicamentoToRemove.id) {
        setMedicamentoToEdit(null)
      }
    },
    [medicamentoToEdit, onClearMedicamentosToReplicate, remove, setMedicamentoToEdit, value]
  )

  const handleOnRemoveMedicamentoToReplicate = useCallback(
    (medicamento: MedicamentoFormModel) => {
      const medicamentos = medicamentosToReplicate.filter((item) => item.id !== medicamento.id)
      if (isEmpty(medicamentos)) {
        onClearMedicamentosToReplicate()
      } else {
        onChangeMedicamentosToReplicate(medicamentos)
      }
      if (medicamentoToEdit?.id === medicamento.id) {
        setMedicamentoToEdit(null)
      }
    },
    [
      medicamentoToEdit,
      medicamentosToReplicate,
      onChangeMedicamentosToReplicate,
      onClearMedicamentosToReplicate,
      setMedicamentoToEdit,
    ]
  )

  const handleOnSubmit = useCallback(
    (medicamento: MedicamentoFormModel) => {
      if (!!medicamentoToEdit) {
        if (medicamentosToReplicate && medicamentosToReplicate.find((item) => item.id === medicamento.id)) {
          push(medicamento)
          handleOnRemoveMedicamentoToReplicate(medicamento)
        } else {
          const index = value.findIndex((item) => item.id === medicamento.id)
          update(index, medicamento)
        }
      } else {
        push(medicamento)
      }
    },
    [handleOnRemoveMedicamentoToReplicate, medicamentoToEdit, medicamentosToReplicate, push, update, value]
  )

  const handleOnMedicamentoDuplicated = useCallback(
    (medicamentoToAdd: MedicamentoFormModel) => {
      return value?.some(
        (receita) =>
          !medicamentoToEdit &&
          !medicamentoToAdd.registroManual &&
          receita.principioAtivoCombo?.medicamento.id === medicamentoToAdd.principioAtivoCombo?.medicamento.id
      )
    },
    [medicamentoToEdit, value]
  )

  return useMemo(
    () => (
      <React.Fragment>
        <HFlow>
          <ModalBody style={styles.formContainer}>
            <TitleGroup
              title='Adicionar prescrição'
              style={css`
                margin-bottom: 1rem;
              `}
            />
            <VFlow vSpacing={2}>
              <PrescricaoMedicamentoForm
                onSubmit={handleOnSubmit}
                isReplicar={!isEmpty(medicamentosToReplicate)}
                medicamentoToEdit={medicamentoToEdit}
                onClearEditedMedicamento={handleOnChangeEditedMedicamento}
                onVerifyMedicamentoDuplicated={handleOnMedicamentoDuplicated}
              />
            </VFlow>
          </ModalBody>
          <VFlow vSpacing={0.5} style={styles.listContainer}>
            {medicamentosToReplicate && !isEmpty(medicamentosToReplicate) && (
              <Alert type='warning' inline>
                Revise os dados antes de salvar uma nova prescrição.
              </Alert>
            )}
            {value?.map((item) => (
              <MedicamentoCard
                key={item.id}
                editing={medicamentoToEdit?.id === item.id}
                medicamentoModel={item}
                onRemoveMedicamento={handleOnRemoveMedicamento}
                onEditMedicamento={setMedicamentoToEdit}
              />
            ))}
            {medicamentosToReplicate &&
              !isEmpty(medicamentosToReplicate) &&
              medicamentosToReplicate.map((item) => (
                <MedicamentoCard
                  key={item.id}
                  editing={medicamentoToEdit?.id === item.id}
                  replicating
                  medicamentoModel={item}
                  onRemoveMedicamento={handleOnRemoveMedicamentoToReplicate}
                  onEditMedicamento={setMedicamentoToEdit}
                />
              ))}
          </VFlow>
        </HFlow>
        <ModalFooter style={styles.footer}>
          <HFlow justifyContent='flex-end'>
            <Button onClick={onCloseModal} style={styles.footerButton}>
              Fechar
            </Button>
            <ButtonLink
              kind='primary'
              style={styles.footerButton}
              to={`${match.url}/imprimir`}
              disabled={!value?.length}
              data-testid='ImprimirButton'
            >
              <Icon icon='printerOutline' style={styles.printIcon} />
              Imprimir
            </ButtonLink>
            <Switch>
              <PrivateRoute
                path={`${match.url}/imprimir`}
                permission={[AcessoCbo.ENFERMEIRO, AcessoCbo.MEDICO, AcessoCbo.CIRURGIAO_DENTISTA]}
              >
                <ImpressaoPrescricaoModal
                  medicamentos={value}
                  atendimentoId={atendimentoId}
                  cidadaoDataNascimento={cidadaoDataNascimento}
                  cidadaoSexo={cidadaoSexo}
                />
              </PrivateRoute>
            </Switch>
          </HFlow>
        </ModalFooter>
      </React.Fragment>
    ),
    [
      atendimentoId,
      cidadaoDataNascimento,
      cidadaoSexo,
      handleOnChangeEditedMedicamento,
      handleOnMedicamentoDuplicated,
      handleOnRemoveMedicamento,
      handleOnRemoveMedicamentoToReplicate,
      handleOnSubmit,
      match.url,
      medicamentoToEdit,
      medicamentosToReplicate,
      onCloseModal,
      setMedicamentoToEdit,
      value,
    ]
  )
}

const styles = {
  formContainer: css`
    width: 50rem;
    background-color: ${theme.pallete.surface.main};
    border-right: 1px solid + ${theme.pallete.gray.c80};
  `,
  listContainer: css`
    margin: 4rem 0rem 1rem 0rem;
    align-self: top;
  `,
  footer: css`
    border-top: 1px solid + ${theme.pallete.gray.c80};
  `,
  printIcon: css`
    margin-right: 0.5rem;
  `,
  footerButton: css`
    width: 9rem;
    height: 3rem;
  `,
}
